import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import {
  Grid, GridColumn,
  Button, useSnackbar, Body, Heading, Divider,
  ProgressTracker,
  ProgressTrackerItem,
  Spinner,
  StyledText,
  Alert,
} from "@walmart-web/livingdesign-components";
import DocumentDropzone from "../../../../components/DocumentDropzone/DocumentDropzone";
import {
  MAX_FILE_UPLOAD_SIZE, ACCEPTED_INVOICES_FILES_LABEL, acceptedFilesForInvoices,
  MAX_FILE_UPLOAD_SIZE_INVOICES, ASYNC, SYNC, BULK_INVOICE_CREATE, LNG_BJO_INVOICE_VALIDATION_ERROR,
  PROCESS_STATUS_LOG,
  CREATE_INVOICES_LEDES,
  MAX_FILE_UPLOAD_10MB,
  LEDES_INFO,
  SELECT_FILES,
  FILES_BEING_VALIDATED,
  VALIDATING_FILES,
  KEEP_WINDOW_OPEN,
  INVOICE_BEING_CREATED,
  DRAFT_INVOICE_TOBE_REVIEWED,
  VALIDATE_FILES,
  FILES_VALIDATED,
  ERROR_PROMPT,
  UPLOAD_FILES,
  VALIDATE_FILES_LABEL,
  REVIEW_ERRORS,
  FILE_NAME,
  INVOICE_NO,
  LINE_ITEMS_LABEL,
  DETAILS,
  FILES_HAVE_ERRORS,
  ALL_FILES_VALIDATED,
  DOWNLOAD_ERRORS,
} from "../../../../constants/constants";
import { postServiceWithHeaders } from "../../../../axios/axios";
import { JOB_SERVICE } from "../../../../constants/baseURLs";
import { renderTernary } from "../../../../helpers/utils";
import {
  VALIDATE_AND_SUBMIT,
  VALIDATE_FILES_AND_UPLOAD
} from "../../../../constants/actionButtons";
import {
  FILE_UPLOAD_SIZE_LIMIT_MSG, SOMETHING_WENT_WRONG,
  INVOICE_UPLOAD_MSG,
  INVOICE_UPLOAD_MSG_TITLE
} from "../../../../constants/messages";
import {
  invoiceErrorMessage,
} from "../../../../constants/templates";
import UploadInvoiceStyles from "./UploadInvoice.styles";
import { FEATURES } from "../../../../constants/features";
import { styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import Paper from '@mui/material/Paper';
import { tableCellClasses } from '@mui/material/TableCell';
import { CloudDownload } from "@livingdesign/icons";

/**
 * Function to format file data
 * @param {string} files
 * @returns array of files data
 */
export const formatFilesData = (files) => {
  const list = [];
  files?.map(item => {
    list.push({
      id: item?.name?.split('.').slice(0, -1).join('.'),
      fileName: item?.name?.split('.').slice(0, -1).join('.'),
      size: item?.size,
    })
  })
  return list;
}

/** Component for Invoice upload */
const UploadInvoice = (props) => {
  const { setIsCurrentTab } = props;
  const classes = UploadInvoiceStyles();
  const history = useHistory();
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [filesFormData, setFilesFormData] = useState();
  const [failedFilesList, setFailedFilesList] = useState([]);
  const [successCount, setSuccessCount] = useState(0);
  const [downloadMessage, setDownloadMessage] = useState([]);
  const [failuresTableData, setFailuresTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState(0);
  const [fileReadLoading, setFileReadLoading] = useState(false);
  const { addSnack } = useSnackbar();
  const orgId = useSelector((state) => state?.organization?.organizationDetailValues.identifier);
  const featureMap = useSelector((state) => state?.user?.featureMap);
  const [trackerIndex, setTrackerIndex] = useState(0);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#7e7e7e",
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));
  const isPreValidationOrg = useSelector((state) => state?.user?.prevalidationEnabled);

  /**
   * Callback to handle when file is dropped in dropzone
   * @param {array} files
   */
  const handleFileUpload = (files) => {
    let newFiles = files;
    setFilesFormData(newFiles);
    const uploadedDocs = formatFilesData(newFiles);
    setFilesToUpload(uploadedDocs)
  }

  const handleErrorReportDownload = (finalMessages) => {
    const fileData = finalMessages.join('\n');
    const blob = new Blob([fileData], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString();
    link.download = "eWMMS_Invoice_Upload_Errors_" + timestamp + ".txt";
    link.href = url;
    link.click();
  }

  const getInvoiceErrorMsg = (result, fileName) => {
    const errResponse = JSON.parse(result?.reason?.response?.data)
    if (errResponse?.errorCode === LNG_BJO_INVOICE_VALIDATION_ERROR) {
      let errorMsg = ""
      Object.entries(JSON.parse(errResponse?.message))?.forEach(([key, value], index) => {
        errorMsg = errorMsg + invoiceErrorMessage({ value: value, index: index }) + "\n"
      });
      return `${fileName} - \n${errorMsg}`;
    }
    else if (fileName && errResponse?.message) {
      return `${fileName} - \n${errResponse?.message}`;
    } else {
      return SOMETHING_WENT_WRONG;
    }
  }

  const getInvoiceErrorMsgForTable = (result, fileName) => {
    try {
      const errResponse = JSON.parse(result?.reason?.response?.data)
      if (errResponse?.errorCode === LNG_BJO_INVOICE_VALIDATION_ERROR) {
        let errors = [];
        Object.entries(JSON.parse(errResponse?.message))?.forEach(([key, value], index) => {
          errors.push({ fileName: fileName, ...value });
        });
        return errors;
      } else {
        return [{ fileName: "-", invoiceNumber: "-", lineitemNumbers: "-", errorMessage: errResponse?.message }];
      }
    } catch {
      return [{ fileName: "-", invoiceNumber: "-", lineitemNumbers: "-", errorMessage: SOMETHING_WENT_WRONG }];
    }
  }

  /**
   * UseEffect to validate file upload size & enable/disable upload button
   */
  useEffect(() => {
    if (filesToUpload?.length > 0) {
      const totalUploadSize = filesToUpload.reduce((acc, obj) => acc + obj['size'], 0);
      setDisableSaveButton(totalUploadSize > MAX_FILE_UPLOAD_SIZE)
      if (totalUploadSize > MAX_FILE_UPLOAD_SIZE) {
        addSnack({
          message: FILE_UPLOAD_SIZE_LIMIT_MSG
        });
      }
    } else {
      setDisableSaveButton(true)
    }
  }, [filesToUpload])

  /** Callback function to handle file drop */
  const handleOnDrop = () => {
    setFileReadLoading(true)
  }

  /** Function to return back to previous screen on save button click */
  const handleSaveUpload = () => {
    if (filesFormData?.length > 0) {
      setTrackerIndex(1);
      const promises = [];
      filesFormData?.forEach((file) => {
        setLoading(true); const headers = {
          'Content-Type': `multipart/form-data`,
        }
        const requestData = {
          file: file,
          executionType: ASYNC,
          validationType: SYNC,
          command: BULK_INVOICE_CREATE,
          dataBag: JSON.stringify({ "organizationId": orgId, "isPreValidation": isPreValidationOrg })
        }
        promises.push({ promise: postServiceWithHeaders(JOB_SERVICE, "/jobs/v1/submit", requestData, headers), request: requestData });
      })

      // Resolve the file upload(s)
      Promise.allSettled(promises.map(obj => obj.promise))
        .then((res) => {
          let isFileUploadFailed = false;
          let failures = [];
          let failureTableData = []
          const messages = res.map((result, index) => {
            const fileName = promises[index]?.request?.file?.name;
            if (result?.status !== 'fulfilled') {
              isFileUploadFailed = true;
              try {
                const errResponse = getInvoiceErrorMsgForTable(result, fileName);
                failureTableData.push(...errResponse)
                failures.push({ fileName: fileName })
              } catch {
                // Fail Silently
              }
              return getInvoiceErrorMsg(result, fileName);
            }
          });

          // Removing undefined from the array
          const finalMessages = messages.filter(item => item !== undefined);
          setSuccessCount(filesFormData?.length - failures?.length)
          setFailedFilesList(failures)
          setFailuresTableData(failureTableData);
          setLoading(false);
          if (isFileUploadFailed) {
            setTrackerIndex(3)
            setDownloadMessage(finalMessages)
            handleErrorReportDownload(finalMessages);
          } else {
            setTrackerIndex(2)
          }
          setKey(key + 1)
        })
        .catch((error) => {
          setLoading(false);
          setKey(key + 1)
        });
    }
  }

  /**  Reset dropzone */
  useEffect(() => {
    setKey(key + 1)
  }, [])

  const SectionButtonsGroup = () => {
    return <GridColumn>
      <div className={`${classes.contentSpacing24} ${classes.uploadButtonWrap}`}>
        <Button
          id="upload-another-ledes"
          data-testid="upload-another-ledes"
          size="small"
          variant="tertiary"
          onClick={() => {
            setTrackerIndex(0)
          }}
        >
          {"Upload other LEDES file(s)"}
        </Button>
        <Button
          id="goto-invoice-btn"
          data-testid="goto-invoice-btn"
          size="small"
          variant="primary"
          onClick={() => (trackerIndex === 2 && isPreValidationOrg) ? setIsCurrentTab(1, true) : setIsCurrentTab(1)}
        >
          {"Go to Invoices"}
        </Button>
      </div>
    </GridColumn>
  }

  const FileValidationInProgress = () => {
    return (
      <div className={classes.fileValidationInProgressWrapper}>
        <div>
          <span><Spinner color="gray" /></span>
          <h1 className={classes.heading}>{VALIDATING_FILES}</h1>
          <Divider style={{ marginBottom: 20 }} />

          <Body as="p" size="medium" weight={400}>
            {FILES_BEING_VALIDATED(filesToUpload?.length)}
          </Body>
          <Body as="p" size="medium" weight={400}>
            {KEEP_WINDOW_OPEN}
          </Body>
        </div>
      </div>
    )
  }

  const FileValidationSuccessResult = () => {
    return (
      <>
        <GridColumn>
          <div>
            <Alert variant="success">{ALL_FILES_VALIDATED}</Alert>
            <Divider style={{ marginBottom: '20px' }} />
            <StyledText color="gray" size="small">
              <span>{INVOICE_BEING_CREATED}</span>
            </StyledText>
            <StyledText color="gray" size="small">
              <span>Once created, all the invoices are available in the <a className={classes.hyperLink} onClick={() => setIsCurrentTab(1)}>{` Financials > Invoices `}</a>. {DRAFT_INVOICE_TOBE_REVIEWED}</span>
            </StyledText>
          </div>
        </GridColumn>
        <SectionButtonsGroup />
      </>
    )
  }

  const FileValidationErrorOrPartialResult = () => {
    return (
      <>
        <GridColumn>
          <h1 className={classes.heading}>{VALIDATE_FILES}</h1>
          {successCount > 0 && <>
            <Alert variant="success">{FILES_VALIDATED(successCount)}</Alert>
            <StyledText color="gray" size="small">
              <span>All created invoices are available in the <a className={classes.hyperLink} onClick={() => setIsCurrentTab(1)}>{` Financials > Invoices `}</a>. {DRAFT_INVOICE_TOBE_REVIEWED}</span>
            </StyledText>
          </>}
          <Alert variant="error" style={{ position: "relative" }}>
            {FILES_HAVE_ERRORS(failedFilesList?.length)}
            <Button className={classes.downloadButton} variant="tertiary" leading={<CloudDownload size="small" />} onClick={() => handleErrorReportDownload(downloadMessage)}>
              {DOWNLOAD_ERRORS}
            </Button>
          </Alert>
          <StyledText color="gray" size="small">
            <span>{ERROR_PROMPT}</span>
          </StyledText>
          {failuresTableData?.length > 0 &&
            <div style={{ margin: 15, height: 500, overflow: "auto" }}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>{FILE_NAME}</StyledTableCell>
                      <StyledTableCell>{INVOICE_NO}</StyledTableCell>
                      <StyledTableCell>{LINE_ITEMS_LABEL}</StyledTableCell>
                      <StyledTableCell>{DETAILS}</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failuresTableData.map((item, index) => (
                      <StyledTableRow
                        key={item.lineItemNumbers + index}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <StyledTableCell>{item?.fileName}</StyledTableCell>
                        <StyledTableCell>{item?.invoiceNumber || "-"}</StyledTableCell>
                        <StyledTableCell>{item?.lineitemNumbers?.length > 0 ? item?.lineitemNumbers.toString() : "-"}</StyledTableCell>
                        <StyledTableCell>{item?.errorMessage || "-"}</StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          }
        </GridColumn>
        <SectionButtonsGroup />
      </>
    )
  }


  const TabContent = () => {
    switch (trackerIndex) {
      case 0:
        return <>
          <GridColumn lg="12" data-testid="document-dropzone" id="document-dropzone">
            <Heading as="h4" UNSAFE_className={classes.uploadInvoicesTitle}>
              {CREATE_INVOICES_LEDES}
            </Heading>
            <DocumentDropzone
              key={key}
              showFilterIcon={true}
              acceptedFilesLabel={ACCEPTED_INVOICES_FILES_LABEL}
              acceptedFileTypes={acceptedFilesForInvoices}
              onFileUpload={handleFileUpload}
              onFileDrop={handleOnDrop}
              fileReadLoading={fileReadLoading}
              fileDropStatus={true}
              showFilePreview={true}
              hideInfoMsg={true}
              maxFileUploadSize={MAX_FILE_UPLOAD_SIZE_INVOICES}
              fileUploadInfoMsg={<div>
                <span style={{ display: 'block' }}>{MAX_FILE_UPLOAD_10MB}</span>
                <span style={{ display: 'block' }}>{LEDES_INFO}</span>
                <span style={{ display: 'block' }}>Looking for updates on invoice creation? Please refer to <a className={classes.hyperLink} onClick={() => setIsCurrentTab(2)}>Process status tab under Financials</a> for updates on invoice creation or look for an email post completion.</span>
              </div>}
              dropZoneLabel={SELECT_FILES}
            />
          </GridColumn>
          <GridColumn>
            <div className={`${classes.contentSpacing24} ${classes.uploadButtonWrap}`}>
              {renderTernary(filesToUpload?.length > 0,
                <Button
                  size="medium" variant="primary"
                  disabled={disableSaveButton || fileReadLoading || loading}
                  onClick={handleSaveUpload}
                  data-testid="save-upload" id="save-upload">
                  {featureMap?.[FEATURES.INVOICE_PRE_VALIDATION] ? VALIDATE_FILES_AND_UPLOAD : VALIDATE_AND_SUBMIT}
                </Button>, <></>
              )}
            </div>
          </GridColumn>
        </>
      case 1:
        return <FileValidationInProgress />
      case 2:
        return <FileValidationSuccessResult />
      case 3:
        return <FileValidationErrorOrPartialResult />
      default:
        return <></>
    }
  };

  return (
    <Grid data-testid="invoice-upload">
      <GridColumn sm={12}>
        <div className={classes.dropzoneWrap}>
          <Grid>
            <GridColumn>
              <div style={{ margin: '20px 0px', paddingBottom: 20, borderBottom: '1px solid #ddd' }}>
                <ProgressTracker activeIndex={trackerIndex} variant="info" style={{ margin: '10' }}>
                  <ProgressTrackerItem>{UPLOAD_FILES}</ProgressTrackerItem>
                  <ProgressTrackerItem>{VALIDATE_FILES_LABEL}</ProgressTrackerItem>
                  <ProgressTrackerItem>{REVIEW_ERRORS}</ProgressTrackerItem>
                </ProgressTracker>
              </div>
            </GridColumn>
            {TabContent()}
          </Grid>
        </div>
      </GridColumn>
    </Grid>
  );
};

export default UploadInvoice;
